import {
  AbstractControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
} from "@angular/forms";

import moment from "moment";
import { StaticClass } from "src/components/shared-components/class/general-classes/static-classes";
import { ValidatorsError } from "src/model/enum/enumFormValidators";
import { DateTimeStringFomat } from "src/components/shared-components/enum/enumMisc";
import { MomentFormats } from "src/model/class/general classes/moment-formats";

/**
 * Handle form validators for date
 */
export class DateValidators {
  /**This validator checks if the string date assigned to "startDate"/"endDate" FormConrol is NOT empty  */
  static isDateNotEmpty(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let validationError = null;
      if (control.value === "") {
        validationError = {};
        validationError[ValidatorsError.DATE_EMPTY] = true;
      }
      return validationError;
    };
  }

  /**This validator checks if the string date assigned to "startDate"/endDate FormControl follows the DESIRED pattern "DD-MM-YYYY"; ALSO, it checks
   * if the date-string is otherwise correct, but follows other, incorrect patterns. These last ones includes:
   *
   * * D-MM-YYYY
   *
   * * DD-M-YYYY
   *
   * * D-M-YYYY
   */
  static isDateFormatCompletelyCorret(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let validationError = null;

      /*Checks if the string date assigned to "startDate"/"endDate" FormConrol follows the UNWANTED pattern "D-MM-YYYY";  if so, it return error*/
      if (
        StaticClass.checkDateTimeStringFormat(
          control.value,
          DateTimeStringFomat.SHORT_DATE_STRAIGHT_SIGLE_DAY
        )
      ) {
        validationError = {};
        validationError[ValidatorsError.DATE_INVALID_FORMAT_SINGLE_DIGIT_DAY] =
          true;

        return validationError;
      }

      /*Checks if the string date assigned to "startDate"/"endDate" FormConrol follows the UNWANTED pattern "DD-M-YYYY"; if so, it return error*/
      if (
        StaticClass.checkDateTimeStringFormat(
          control.value,
          DateTimeStringFomat.SHORT_DATE_STRAIGHT_SIGLE_MONTH
        )
      ) {
        validationError = {};
        validationError[
          ValidatorsError.DATE_INVALID_FORMAT_SINGLE_DIGIT_MONTH
        ] = true;

        return validationError;
      }

      /*Checks if the string date assigned to "startDate"/"endDate" FormConrol follows the UNWANTED pattern "D-M-YYYY"; if so, it return error*/
      if (
        StaticClass.checkDateTimeStringFormat(
          control.value,
          DateTimeStringFomat.SHORT_DATE_STRAIGHT_SIGLE_DAY_MONTH
        )
      ) {
        validationError = {};
        validationError[
          ValidatorsError.DATE_INVALID_FORMAT_SINGLE_DIGIT_DAY_MONTH
        ] = true;

        return validationError;
      }

      /*Checks if the string time assigned to "startDate"/"endDate" FormControl follows the UNWANTED pattern "DDMMYYYY";  if so, it return error*/
      if (
        StaticClass.checkDateTimeStringFormat(
          control.value,
          DateTimeStringFomat.SHORT_DATE_STRAIGHT_NO_SEPARATORS
        )
      ) {
        validationError = {};
        validationError[ValidatorsError.DATE_INVALID_FORMAT_NO_SEPARATOR] =
          true;
        return validationError;
      }

      /*FINALLY, checks if the string date assigned to "startDate"/"endDate" FormConrol follows the NEEDED pattern "DD-MM-YYYY"; otherwise, it return error*/
      if (
        !StaticClass.checkDateTimeStringFormat(
          control.value,
          DateTimeStringFomat.SHORT_DATE_STRAIGHT
        )
      ) {
        validationError = {};
        validationError[ValidatorsError.DATE_INVALID_FORMAT] = true;
      }

      return validationError;
    };
  }

  /**This validator checks if the string date assigned to "startDate"/endDate FormControl follows the DESIRED pattern "DD-MM-YYYY"; ALSO, it checks
   * if the date-string is otherwise correct, but follows other, incorrect patterns. These last ones includes:
   *
   * * D-MM-YYYY
   *
   * * DD-M-YYYY
   *
   * * D-M-YYYY
   */
  static isDateFormatYYYYMMDDCorrect(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let validationError = null;

      /*FINALLY, checks if the string date assigned to "startDate"/"endDate" FormConrol follows the NEEDED pattern "DD-MM-YYYY"; otherwise, it return error*/
      if (
        !StaticClass.checkDateTimeStringFormat(
          control.value,
          DateTimeStringFomat.SHORT_DATE_INVERSE
        )
      ) {
        validationError = {};
        validationError[ValidatorsError.DATE_INVALID_FORMAT] = true;
      }

      return validationError;
    };
  }

  /**
   * Check if generic date is less then another
   *
   * @param fromControlName From control name
   * @param toControlName To Control name
   * @returns
   */
  static dateLessThan(
    fromControlName: string,
    toControlName: string
  ): ValidatorFn {
    return (group: UntypedFormGroup): ValidationErrors | null => {
      let validatorError = null;
      let fromControl: AbstractControl = group.controls[fromControlName];
      let toControl: AbstractControl = group.controls[toControlName];

      if (
        fromControl.value !== "" &&
        toControl.value != "" &&
        moment(
          fromControl.value,
          MomentFormats.MOMENT_DATETIME_FORMAT_SHORT_DATE_STRAIGHT
        ).isSameOrAfter(
          moment(
            toControl.value,
            MomentFormats.MOMENT_DATETIME_FORMAT_SHORT_DATE_STRAIGHT
          )
        )
      ) {
        validatorError = {};

        validatorError[ValidatorsError.DATES_CONFLICT] = true;
      }

      return validatorError;
    };
  }

  /**
   * Check if generic date is less then another
   *
   * @param fromControlName From control name
   * @param toControlName To Control name
   * @returns
   */
  static dateLessThanYYYYMMDD(
    fromControlName: string,
    toControlName: string
  ): ValidatorFn {
    return (group: UntypedFormGroup): ValidationErrors | null => {
      let validatorError = null;
      let fromControl: AbstractControl = group.controls[fromControlName];
      let toControl: AbstractControl = group.controls[toControlName];

      if (
        fromControl.value !== "" &&
        toControl.value != "" &&
        moment(
          fromControl.value,
          MomentFormats.MOMENT_DATETIME_FORMAT_SHORT_DATE_INVERSE
        ).isSameOrAfter(
          moment(
            toControl.value,
            MomentFormats.MOMENT_DATETIME_FORMAT_SHORT_DATE_INVERSE
          )
        )
      ) {
        validatorError = {};

        validatorError[ValidatorsError.DATES_CONFLICT] = true;
      }

      return validatorError;
    };
  }

  /**
   * Check if generic date (format = "YYYY-MM-DDTHH:mm:ss") is less then another
   *
   * @param fromControlName From control name
   * @param toControlName To Control name
   * @returns
   */
  public static dateTimeLessThan(
    fromControlName: string,
    toControlName: string
  ): ValidatorFn {
    return (group: UntypedFormGroup): ValidationErrors | null => {
      let validatorError = null;
      let fromControl: AbstractControl = group.controls[fromControlName];
      let toControl: AbstractControl = group.controls[toControlName];

      const startMoment = moment(
        fromControl.value,
        MomentFormats.MOMENT_DATETIME_FORMAT_DEVICE_VALID_LIST
      );
      const endMoment = moment(
        toControl.value,
        MomentFormats.MOMENT_DATETIME_FORMAT_DEVICE_VALID_LIST
      );

      if (
        fromControl.value !== "" &&
        toControl.value != "" &&
        moment(
          fromControl.value,
          MomentFormats.MOMENT_DATETIME_FORMAT_DEVICE_VALID_LIST
        ).isSameOrAfter(
          moment(
            toControl.value,
            MomentFormats.MOMENT_DATETIME_FORMAT_DEVICE_VALID_LIST
          )
        )
      ) {
        validatorError = {};
        validatorError[ValidatorsError.DATES_CONFLICT] = true;
      }

      return validatorError;
    };
  }

  public static dateTimeLessThanSingleForm(
    upperGroup: UntypedFormGroup,
    fromControlName: string,
    toControlName: string
  ): ValidatorFn {
    return (): ValidationErrors | null => {
      let validatorError = null;
      let fromControl: AbstractControl = upperGroup.controls[fromControlName];
      let toControl: AbstractControl = upperGroup.controls[toControlName];
      if (
        fromControl.value !== "" &&
        toControl.value != "" &&
        moment(
          fromControl.value,
          MomentFormats.MOMENT_DATETIME_FORMAT_DEVICE_VALID_LIST
        ).isSameOrAfter(
          moment(
            toControl.value,
            MomentFormats.MOMENT_DATETIME_FORMAT_DEVICE_VALID_LIST
          )
        )
      ) {
        validatorError = {};
        validatorError[ValidatorsError.DATES_CONFLICT] = true;
      }

      return validatorError;
    };
  }

  /**
   * Checks that the start date is not greater than the end date
   * @param group FormGroup
   * @returns validation error or null
   *
   */
  public static dateRangeValidator(group: UntypedFormGroup): ValidationErrors | null {
    const fromDate = group.controls["fromDate"].value;
    const toDate = group.controls["toDate"].value;
    if (!fromDate || !toDate) {
      return null;
    }
    const from = new Date(fromDate);
    const to = new Date(toDate);
    if (from > to) {
      return { dateRangeInvalid: true };
    }
    return null;
  }
}
